/**
 * MandateBadgeTile - the per-mandate-type Mandatskachel in the customer header (P4-S1, 7.6, 10.5).
 *
 * The state and the German label are API-computed (MandateBadgeService); this tile only formats
 * the date/time, appends the Mahnstufe suffix when > 0, colours by state, and (when the tile
 * carries a Vorgangs-ID) scrolls to that Vorgang row in the Uebersicht on a click (O10). There is
 * no mandate/Vorgang detail screen yet, so the click is a scroll, not a navigation.
 */

import type { MandateBadgeWire } from '../../../lib/api-client';
import { vorgangAnchorId } from './KundeVorgangCard';
import { dateTimeShortDe } from '../../../lib/format';

/** Tailwind classes per state (grey / coral / green), matching the status language elsewhere. */
const STATE_CLASSES: Record<MandateBadgeWire['state'], string> = {
  nicht_erteilt: 'bg-muted text-muted-foreground',
  in_verarbeitung: 'bg-orange-100 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200',
  unterzeichnet: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  // P6: a mandate the Freigabe ended per a date (amber until the date, grey once ended).
  gekuendigt: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  beendet: 'bg-muted text-muted-foreground',
};

export function MandateBadgeTile({
  badge,
  onSelectTab,
}: {
  badge: MandateBadgeWire;
  onSelectTab?: (tab: string) => void;
}) {
  // Build the detail line: label · date/time · actor · Erinnerung N (suffix only when > 0).
  const parts: string[] = [badge.label];
  const when = dateTimeShortDe(badge.at);
  if (when !== '') parts.push(when);
  if (badge.actorLabel) parts.push(badge.actorLabel);
  if (badge.mahnStage > 0) parts.push(`Erinnerung ${badge.mahnStage}`);
  const text = parts.join(' · ');

  const clickable = badge.vorgangId !== null;
  const onClick = () => {
    if (badge.vorgangId === null) return;
    onSelectTab?.('uebersicht');
    const el = typeof document !== 'undefined' ? document.getElementById(vorgangAnchorId(badge.vorgangId)) : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(vorgangAnchorId(badge.vorgangId!));
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  return (
    <button
      type="button"
      data-testid={`mandate-badge-${badge.kind}`}
      aria-label={text}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`rounded-md px-2 py-1 text-xs font-medium ${STATE_CLASSES[badge.state]} ${clickable ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
    >
      {text}
    </button>
  );
}
